<template>
  <div>
    <div class="column">
      <div class="col q-pb-sm text-grey-brand text-bold">
        User Notifications
      </div>
      <div class="col q-pb-sm">
        <q-btn
          dense
          size="sm"
          class="btn-brand"
          icon="fas fa-plus"
          label="Add User Notification"
          no-caps
          @click="onAddUserNotification"
        />&nbsp;
      </div>
      <div class="col q-pb-sm">
        <q-table
          v-model:pagination="infinitePagination"
          dense
          flat
          title=""
          :rows="notificationTable"
          :columns="userNotificationColumns"
          row-key="userId"
          no-data-label="No user notifications added"
          hide-pagination
          :visible-columns="userNotificationVisibleColumns"
        >
          <template #body-cell-info-user="props">
            <q-td :props="props">
              <q-btn
                class="btn-brand"
                size="xs"
                dense
                icon="fas fa-info-circle"
                @click="onUserNotificationInfo(props)"
              ></q-btn>
            </q-td>
          </template>
          <template #body-cell-delete-user="props">
            <q-td :props="props">
              <q-btn
                class="btn-brand"
                size="xs"
                dense
                icon="fas fa-trash"
                @click="onDeleteUserNotification(props)"
              ></q-btn>
            </q-td>
          </template>
        </q-table>
      </div>
    </div>

    <q-dialog v-model="infoNotifierDialog">
      <q-card style="width: 350px; max-width: 90vw">
        <q-toolbar>
          <div class="text-blue-brand text-bold">User Notification</div>
          <q-space />
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>
        <q-card-section v-if="selectedUserNotification">
          <div class="q-pa-sm">User: {{ selectedUserNotification.name }}</div>
          <div class="q-pa-sm">
            Launch: {{ selectedUserNotification.launch }}
          </div>
          <div class="q-pa-sm">
            Receive: {{ selectedUserNotification.receive }}
          </div>
          <div class="q-pa-sm">
            Medium: {{ selectedUserNotification.medium }}
          </div>
          <div class="q-pa-sm">
            Passage Type: {{ selectedUserNotification.passageType }}
          </div>
          <div class="q-pa-sm">
            Include Manual: {{ selectedUserNotification.manualPassage }}
          </div>
          <div class="q-pa-sm">
            All Markers: {{ selectedUserNotification.allMarkers }}
          </div>
          <div v-if="!selectedUserNotification.allMarkers" class="q-pa-sm">
            Markers: {{ selectedUserNotification.markers }}
          </div>
          <q-separator />
          <q-card-actions align="center">
            <q-btn v-close-popup class="btn-brand" label="Close" no-caps />
          </q-card-actions>
        </q-card-section>
      </q-card>
    </q-dialog>

    <q-dialog v-model="addNotifierDialog">
      <q-card style="width: 350px; max-width: 90vw">
        <q-toolbar>
          <div class="text-blue-brand text-bold">Add User Notification</div>
          <q-space />
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>
        <q-card-section>
          <div class="q-pb-sm">
            <q-select
              v-model="selectedUser"
              dense
              :options="userOptions"
              color="blue-brand"
              label="User"
              filled
            />
          </div>
          <div class="q-pa-sm">
            <div class="q-mb-md">Notification Settings</div>

            <q-card flat bordered>
              <q-radio
                v-model="medium"
                color="blue-brand"
                val="sms"
                label="SMS"
              />
              <q-radio
                v-model="medium"
                color="blue-brand"
                val="email"
                label="E-mail"
              />
              <q-radio
                v-model="medium"
                color="blue-brand"
                val="both"
                label="Both"
              />
              <q-radio
                v-model="medium"
                color="blue-brand"
                val="none"
                label="None"
              />
            </q-card>

            <q-card flat bordered class="q-mt-md">
              <q-checkbox
                v-model="launch"
                color="blue-brand"
                label="At Launch"
              /><br />
              <q-checkbox
                v-model="receive"
                color="blue-brand"
                label="At Receive"
              /><br />
            </q-card>

            <q-card flat bordered class="q-mt-md">
              <q-radio
                v-model="passageType"
                color="blue-brand"
                val="verified"
                label="Verified Passages"
              /><br />
              <q-radio
                v-model="passageType"
                color="blue-brand"
                val="passage"
                label="All Passages (excluding manual)"
              /><br />
              <q-checkbox
                v-model="manualPassages"
                color="blue-brand"
                label="Include Manual Passages"
              /><br />
            </q-card>

            <q-card flat bordered class="q-mt-md">
              <q-checkbox
                v-model="allMarkers"
                color="blue-brand"
                label="All Markers"
              />
              <q-select
                v-if="!allMarkers"
                v-model="tmpSelectedMarkers"
                dense
                filled
                color="blue-brand"
                multiple
                :options="markerOptions"
                use-chips
                stack-label
                label="Markers"
                style="width: 300px"
              />
            </q-card>
          </div>

          <q-card-actions align="center">
            <q-btn v-close-popup class="btn-brand" label="Cancel" no-caps />
            <q-btn
              v-close-popup
              :disable="selectedUser == ''"
              class="btn-brand"
              label="Save"
              no-caps
              @click="onUserNotificationAdded"
            />
          </q-card-actions>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { UserNotification } from '@/models/UserNotification';
import { UserMgmt } from '@/models/UserMgmt';

export default defineComponent({
  name: 'SmsNotifications',
  props: {
    markers: { type: Array, default: () => [] },
    notifications: { type: Array, default: () => [] },
  },
  emits: ['update-notifications'],
  data() {
    return {
      addNotifierDialog: false,
      infoNotifierDialog: false,
      launch: true,
      receive: true,
      medium: 'sms',
      passageType: 'verified',
      manualPassages: true,
      allMarkers: true,
      tmpSelectedMarkers: [] as string[],
      markerOptions: [] as string[],
      selectedUser: '' as any,
      selectedUserNotification: null as any,
      userNotifications: [] as UserNotification[],
      infinitePagination: { rowsPerPage: 0 },
      userNotificationVisibleColumns: ['name', 'info-user', 'delete-user'],
      userNotificationColumns: [
        { name: 'name', label: 'Name', field: 'name', sortable: false },
        { name: 'userId', label: 'UserID', field: 'userId', sortable: false },
        { name: 'launch', label: 'Launch', field: 'launch', sortable: false },
        {
          name: 'receive',
          label: 'Receive',
          field: 'receive',
          sortable: false,
        },
        {
          name: 'passageType',
          label: 'Passage Type',
          field: 'passageType',
          sortable: false,
        },
        {
          name: 'manualPassage',
          label: 'Manual Passage',
          field: 'manualPassage',
          sortable: false,
        },
        {
          name: 'allMarkers',
          label: 'All Markers',
          field: 'allMarkers',
          sortable: false,
        },
        {
          name: 'markers',
          label: 'Markers',
          field: 'markers',
          sortable: false,
        },
        { name: 'info-user', field: 'user', label: '' },
        { name: 'delete-user', field: 'user', label: '' },
      ],
    };
  },
  computed: {
    userOptions(): any[] {
      if (this.$store.state.admin?.users) {
        return this.$store.state.admin.users.map((user: UserMgmt) => {
          return { label: user.name, value: user.id };
        });
      }
      return [];
    },
    notificationTable(): UserNotification[] {
      return this.userNotifications;
    },
  },
  watch: {
    notifications: {
      immediate: true,
      deep: true,
      handler(value: UserNotification[]) {
        this.userNotifications = value ? [...value] : [];
      },
    },
  },
  mounted() {
    this.$store.dispatch('getLocalUsers');
  },
  methods: {
    onAddUserNotification() {
      this.selectedUser = '';
      this.passageType = 'verified';
      this.launch = true;
      this.receive = true;
      this.medium = 'sms';
      this.manualPassages = true;
      this.allMarkers = true;
      this.tmpSelectedMarkers = [];

      this.markerOptions = [];
      if (this.markers) {
        this.markerOptions = (this.markers as any[]).map((m: any) => m.name);
      }

      this.addNotifierDialog = true;
    },
    onUserNotificationAdded() {
      if (!this.selectedUser) return;

      // Remove existing entry for this user (replace on duplicate)
      const idx = this.userNotifications.findIndex(
        (n) => n.userId === this.selectedUser.value
      );
      if (idx >= 0) {
        this.userNotifications.splice(idx, 1);
      }

      this.userNotifications.push(
        new UserNotification(
          this.selectedUser.label,
          this.selectedUser.value,
          this.launch,
          this.receive,
          this.medium,
          this.passageType,
          this.manualPassages,
          this.allMarkers,
          this.allMarkers ? undefined : this.tmpSelectedMarkers
        )
      );

      this.$emit('update-notifications', this.userNotifications);
    },
    onDeleteUserNotification(props: any) {
      this.userNotifications = this.userNotifications.filter(
        (n: any) => n.userId !== props.row.userId
      );
      this.$emit('update-notifications', this.userNotifications);
    },
    onUserNotificationInfo(props: any) {
      this.selectedUserNotification = props.row;
      this.infoNotifierDialog = true;
    },
  },
});
</script>

<style></style>
